/**
 * decree-lines.ts — dekretacja faktury: model wiersza dekretu (Wn/Ma) + logika
 * grupowania i bilansu, współdzielona przez `DecreeLinesEditor` (edycja) i
 * `DecreeLinesSection` (odczyt).
 *
 * Model wiersza — uproszczenie modelu dekretacji wzorem WF `DocumentAccountingEntry`
 * (migracja `0140_simplify_decree_lines.sql`, `document_decree_lines` +
 * `lib/validators/document-decree.ts`, `DECREE_LINE_SCHEMA`): jedna kwota + `side`
 * per wiersz (NIE dwie kolumny w modelu danych), ale UI prezentuje wiersze jak dwie
 * WIRTUALNE kolumny Wn/Ma (patrz `DecreeLinesEditor`/`DecreeLinesSection` — kwota
 * renderuje się w kolumnie wg `side`). Konto to 6 wolnych segmentów tekstowych
 * (`mainAccount` = Syntetyka + `subAccount1..5` = Pozycja 1-5, wzorem WF
 * `MainAccount`/`SubAccount1..5`), bez FK/walidacji do słownika `chart_of_accounts`.
 *
 * `key` (draft) to stabilny identyfikator klienta (React key) — NIE jest
 * polem z backendu (mirror `DocumentLineItemDraft.key` w `document-items.ts`).
 */

import type { TFunction } from 'i18next';

export type DecreeLineSide = 'debit' | 'credit';

/** Kształt minimalny do liczenia bilansu — wspólny dla `DecreeLineDraft` (edycja)
 * i `DecreeLine` (odczyt), żeby `computeGroupBalance`/`computeOverallBalance` nie
 * duplikowały się per typ. */
export interface DecreeLineLike {
  groupNo: number;
  side: DecreeLineSide;
  amount: string;
}

/** Kształt wspólny obu modeli wiersza dekretu (`DecreeLineDraft` — edycja, `DecreeLine` —
 * odczyt) — WYŁĄCZNIE do typowania listy 6 segmentów konta poniżej (mirror `DecreeLineLike`
 * dla bilansu, ten sam powód: jedna definicja zamiast dwóch zgadzających się kopii). */
export interface DecreeAccountSegments {
  mainAccount: unknown;
  subAccount1?: unknown;
  subAccount2?: unknown;
  subAccount3?: unknown;
  subAccount4?: unknown;
  subAccount5?: unknown;
}

/** Segmenty konta w kolejności wyświetlania — Syntetyka + Pozycja 1-5 (wzorem WF),
 * zawsze widoczne. Współdzielone przez `DecreeLinesEditor` (edycja) i `DecreeLinesSection`
 * (odczyt) — była to duplikacja 1:1 w obu plikach (finding [J]), scalona tutaj. */
export const ACCOUNT_SEGMENT_FIELDS = [
  'mainAccount',
  'subAccount1',
  'subAccount2',
  'subAccount3',
  'subAccount4',
  'subAccount5',
] as const satisfies ReadonlyArray<keyof DecreeAccountSegments>;

export type AccountSegmentField = (typeof ACCOUNT_SEGMENT_FIELDS)[number];

/**
 * Etykieta kolumny segmentu konta — `t()` z LITERAŁEM per gałąź (nie szablonem ze zmienną)
 * celowo: `t(\`...${field}\`)`/`t(zmienna)` są martwym polem skanera `check:i18n` (nie widzi
 * kluczy budowanych dynamicznie — patrz FRONTEND.md §8.2 „Co jest NIEWIDOCZNE dla skanera").
 * Switch z literałami sprawia, że wszystkie 6 kluczy jest wykrywalnych statycznie. NIE
 * refaktoryzować na szablon ze zmienną ani lookup object — złamie to skaner.
 */
export function accountSegmentLabel(field: AccountSegmentField, t: TFunction): string {
  switch (field) {
    case 'mainAccount':
      return t('tenant.documents.decree.columns.mainAccount');
    case 'subAccount1':
      return t('tenant.documents.decree.columns.subAccount1');
    case 'subAccount2':
      return t('tenant.documents.decree.columns.subAccount2');
    case 'subAccount3':
      return t('tenant.documents.decree.columns.subAccount3');
    case 'subAccount4':
      return t('tenant.documents.decree.columns.subAccount4');
    case 'subAccount5':
      return t('tenant.documents.decree.columns.subAccount5');
  }
}

// ─── Znacznik księgowania równoległego (`behind_posting`) ────────────────────

/**
 * Dozwolone wartości znacznika księgowania równoległego — mirror `BEHIND_POSTING_VALUES`
 * z `apps/api/lib/validators/document-decree.ts` (nie da się zaimportować spoza apps/api)
 * i CHECK-a `chk_document_decree_lines_behind_posting`.
 *
 * To ta sama kolumna, którą dokument niesie do Symfonii: `fk.zapisy.zapisRownolegly` w
 * bazie FK, `ParallelType` w DTO WebAPI, `behind_posting` u nas i w WF. Kolejność =
 * kolejność opcji w formatce.
 */
export const BEHIND_POSTING_VALUES = [0, 6, 1, 33] as const;
export type BehindPostingValue = (typeof BEHIND_POSTING_VALUES)[number];

/** Wartość z serwera/drafta → dozwolony znacznik; nieznana (albo brak) → `0` (zapis zwykły). */
export function normalizeBehindPosting(value: number | undefined): BehindPostingValue {
  const candidate = value ?? 0;
  return (BEHIND_POSTING_VALUES as readonly number[]).includes(candidate)
    ? (candidate as BehindPostingValue)
    : 0;
}

/**
 * Opis znaczenia znacznika. `t()` z LITERAŁEM per gałąź (nie szablonem ze zmienną) z tego
 * samego powodu co `accountSegmentLabel` wyżej — klucze budowane dynamicznie są martwym
 * polem skanera `check:i18n`.
 */
export function behindPostingDescription(value: BehindPostingValue, t: TFunction): string {
  switch (value) {
    case 0:
      return t('tenant.documents.decree.behindPosting.plain');
    case 6:
      return t('tenant.documents.decree.behindPosting.source');
    case 1:
      return t('tenant.documents.decree.behindPosting.parallel');
    case 33:
      return t('tenant.documents.decree.behindPosting.additional');
  }
}

/**
 * Etykieta na ROZWINIĘTEJ liście: liczba, która realnie idzie do kolumny `behind_posting`
 * (i dalej do Symfonii), plus jej opis — inaczej wybierając z listy nie widać, jaką wartość
 * się ustawia. Na zwiniętej komórce pokazujemy samą liczbę (`behindPostingLabel`).
 */
export function behindPostingOptionLabel(value: BehindPostingValue, t: TFunction): string {
  return `${value} — ${behindPostingDescription(value, t)}`;
}

/**
 * Etykieta w komórce tabeli (zwinięta kontrolka i widok tylko-do-odczytu) — SAMA WARTOŚĆ
 * liczbowa, bo to ona jest zapisywana i to nią posługuje się Symfonia; opis czeka na
 * rozwiniętej liście i w `title`.
 */
export function behindPostingLabel(value: BehindPostingValue): string {
  return String(value);
}

/** Edytowalny wiersz dekretu (stan grida). Kwoty jako STRINGI dziesiętne
 * (konwencja Drizzle `numeric`, zero utraty precyzji) — formatowanie robi
 * WYŁĄCZNIE warstwa UI. */
export interface DecreeLineDraft {
  /** Stabilny identyfikator klienta (React key) — NIE wysyłany do API. */
  key: string;
  /** Numer grupy podziału („pozycji") — wiersze Wn/Ma tej samej pozycji dzielą `groupNo`. */
  groupNo: number;
  /** Numer wiersza w obrębie CAŁEGO dekretu — nadaje silnik serwerowy przy zapisie
   * (klient nigdy go nie wysyła); obecny tylko na wierszach wczytanych z serwera. */
  lineNumber?: number;
  side: DecreeLineSide;
  /** Konto syntetyczne — wolny tekst, bez słownika (patrz nagłówek modułu). */
  mainAccount: string;
  subAccount1?: string;
  subAccount2?: string;
  subAccount3?: string;
  subAccount4?: string;
  subAccount5?: string;
  amount: string;
  description?: string;
  contractorId?: string;
  /** Znacznik księgowania równoległego — patrz `BEHIND_POSTING_VALUES`. */
  behindPosting?: BehindPostingValue;
  /** Czy zapis zakłada rozrachunek (konta rozrachunkowe). */
  isTransaction?: boolean;
}

/** Wiersz dekretu z odpowiedzi GET (odczyt) — pola nullable zamiast opcjonalnych
 * (mirror nullability kolumn SQL, konwencja `DocumentLineItem` w `document-items.ts`). */
export interface DecreeLine {
  id: string;
  groupNo: number;
  lineNumber: number;
  side: DecreeLineSide;
  mainAccount: string;
  subAccount1: string | null;
  subAccount2: string | null;
  subAccount3: string | null;
  subAccount4: string | null;
  subAccount5: string | null;
  amount: string;
  description: string | null;
  contractorId: string | null;
  /** Kolumny NOT NULL DEFAULT (migracja 0155) — serwer zawsze je zwraca. */
  behindPosting: number;
  isTransaction: boolean;
}

/**
 * Wiersz dekretu z odpowiedzi GET → draft edytowalny (mirror `serverItemToDraft`,
 * `apps/admin/src/lib/documents/document-items-draft.ts`). `key` = `id` serwerowy —
 * stabilny, re-sync po zapisie nie tasuje kluczy React.
 */
export function serverDecreeLineToDraft(line: DecreeLine): DecreeLineDraft {
  return {
    key: line.id,
    groupNo: line.groupNo,
    lineNumber: line.lineNumber,
    side: line.side,
    mainAccount: line.mainAccount,
    subAccount1: line.subAccount1 ?? undefined,
    subAccount2: line.subAccount2 ?? undefined,
    subAccount3: line.subAccount3 ?? undefined,
    subAccount4: line.subAccount4 ?? undefined,
    subAccount5: line.subAccount5 ?? undefined,
    amount: line.amount,
    description: line.description ?? undefined,
    contractorId: line.contractorId ?? undefined,
    behindPosting: normalizeBehindPosting(line.behindPosting),
    isTransaction: line.isTransaction,
  };
}

/**
 * Kształt wiersza dekretu WYSYŁANY w `decreeLines` payloadu POST/PATCH faktury —
 * mirror `DECREE_LINE_SCHEMA` z `apps/api/lib/validators/document-decree.ts` (nie da
 * się zaimportować spoza apps/api). Pola walutowe (`currencyCode`/`exchangeRate`/
 * `amountPln`) i `openItemRef` CELOWO pominięte — `DecreeLineDraft`/`DecreeLinesEditor`
 * nie mają dla nich UI (znana luka, dziedziczona z poprzedniej wersji).
 */
export interface DecreeLinePayload {
  groupNo: number;
  mainAccount: string;
  subAccount1?: string | null;
  subAccount2?: string | null;
  subAccount3?: string | null;
  subAccount4?: string | null;
  subAccount5?: string | null;
  side: DecreeLineSide;
  amount: string;
  description?: string | null;
  contractorId?: string | null;
  behindPosting?: BehindPostingValue;
  isTransaction?: boolean;
}

/** Draft → kształt wysyłany w `decreeLines` (mirror `draftToPayload`,
 * `document-items-draft.ts`). */
export function decreeDraftLineToPayload(line: DecreeLineDraft): DecreeLinePayload {
  return {
    groupNo: line.groupNo,
    mainAccount: line.mainAccount.trim(),
    subAccount1: line.subAccount1?.trim() || null,
    subAccount2: line.subAccount2?.trim() || null,
    subAccount3: line.subAccount3?.trim() || null,
    subAccount4: line.subAccount4?.trim() || null,
    subAccount5: line.subAccount5?.trim() || null,
    side: line.side,
    amount: line.amount,
    description: line.description?.trim() || null,
    contractorId: line.contractorId || null,
    behindPosting: normalizeBehindPosting(line.behindPosting),
    isTransaction: line.isTransaction ?? false,
  };
}

/** Nowy pusty wiersz danej grupy/strony. */
export function emptyDecreeLine(groupNo: number, side: DecreeLineSide): DecreeLineDraft {
  return {
    key: crypto.randomUUID(),
    groupNo,
    side,
    mainAccount: '',
    amount: '',
    behindPosting: 0,
    isTransaction: false,
  };
}

/** Kolejny wolny numer grupy (max istniejącego + 1; 1 gdy pusto). */
export function nextGroupNo(lines: readonly DecreeLineLike[]): number {
  return lines.reduce((max, line) => Math.max(max, line.groupNo), 0) + 1;
}

/** „Dodaj pozycję" — zasiewa PARĘ wierszy Wn+Ma w NOWEJ grupie, dopisaną na końcu. */
export function seedGroup(lines: readonly DecreeLineDraft[]): DecreeLineDraft[] {
  const groupNo = nextGroupNo(lines);
  return [...lines, emptyDecreeLine(groupNo, 'debit'), emptyDecreeLine(groupNo, 'credit')];
}

/** Dopisuje pojedynczy wiersz (jednej strony) do istniejącej grupy. */
export function addLineToGroup(
  lines: readonly DecreeLineDraft[],
  groupNo: number,
  side: DecreeLineSide,
): DecreeLineDraft[] {
  return [...lines, emptyDecreeLine(groupNo, side)];
}

/** Usuwa pojedynczy wiersz po kluczu klienta. */
export function removeDecreeLine(
  lines: readonly DecreeLineDraft[],
  key: string,
): DecreeLineDraft[] {
  return lines.filter((line) => line.key !== key);
}

/** Usuwa całą grupę (wszystkie wiersze Wn/Ma tej pozycji). */
export function removeDecreeGroup(
  lines: readonly DecreeLineDraft[],
  groupNo: number,
): DecreeLineDraft[] {
  return lines.filter((line) => line.groupNo !== groupNo);
}

/** Aktualizuje jeden wiersz (patch scalony przez `key`). */
export function updateDecreeLine(
  lines: readonly DecreeLineDraft[],
  key: string,
  patch: Partial<DecreeLineDraft>,
): DecreeLineDraft[] {
  return lines.map((line) => (line.key === key ? { ...line, ...patch } : line));
}

/** Wiersze pogrupowane po `groupNo`, posortowane rosnąco po numerze grupy — wewnątrz
 * grupy zachowana kolejność wejściowa (stabilne grupowanie). Generyczne po `T` — działa
 * zarówno na `DecreeLineDraft[]` (edycja) jak i `DecreeLine[]` (odczyt). */
export function groupDecreeLines<T extends { groupNo: number }>(
  lines: readonly T[],
): Array<{ groupNo: number; lines: T[] }> {
  const byGroup = new Map<number, T[]>();
  for (const line of lines) {
    const bucket = byGroup.get(line.groupNo);
    if (bucket) bucket.push(line);
    else byGroup.set(line.groupNo, [line]);
  }
  return [...byGroup.entries()]
    .sort(([a], [b]) => a - b)
    .map(([groupNo, groupLines]) => ({ groupNo, lines: groupLines }));
}

export interface DecreeBalance {
  debit: number;
  credit: number;
  /** debit - credit (dodatnie = nadwyżka Wn, ujemne = nadwyżka Ma). */
  diff: number;
  balanced: boolean;
}

/** Tolerancja porównania Wn/Ma (grosze) — kwoty `numeric(14,2)`, chroni przed false
 * negative z binarnej arytmetyki zmiennoprzecinkowej. */
const BALANCE_EPSILON = 0.005;

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Wartość NIEPUSTA, ale nie da się jej sparsować jako liczby — błąd usera (literówka,
 * wklejony tekst), NIE to samo co pole jeszcze puste (`''` sumuje się jako 0, patrz
 * `sumSide`). Współdzielone przez `computeBalance` (wymusza `balanced: false`, żeby
 * cichy 0 nie maskował błędnej wartości) i `AmountCell`/`DecreeLinesEditor` (podświetlenie
 * komórki) — jedno źródło prawdy dla „co znaczy błędna kwota". */
export function isInvalidAmount(amount: string): boolean {
  return amount.trim() !== '' && !Number.isFinite(Number(amount));
}

function sumSide(lines: readonly DecreeLineLike[], side: DecreeLineSide): number {
  return lines.reduce((sum, line) => {
    if (line.side !== side) return sum;
    const n = Number(line.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** Bilans zbioru wierszy — ΣWn vs ΣMa. Wołający decyduje o zakresie: cała grupa
 * (`computeGroupBalance`) albo cały dekret (`computeOverallBalance`, wszystkie grupy razem).
 * Gdy KTÓRYKOLWIEK wiersz ma `amount` niepusty a nienumeryczny — bilans wymuszony na
 * `balanced: false` niezależnie od sumy (przed tą poprawką `sumSide` cicho liczyła taki
 * wiersz jako 0, więc literówka w kwocie mogła „przypadkiem" zbilansować grupę). */
function computeBalance(lines: readonly DecreeLineLike[]): DecreeBalance {
  const debit = sumSide(lines, 'debit');
  const credit = sumSide(lines, 'credit');
  const diff = round2(debit - credit);
  const hasInvalidAmount = lines.some((line) => isInvalidAmount(line.amount));
  return {
    debit: round2(debit),
    credit: round2(credit),
    diff,
    balanced: !hasInvalidAmount && Math.abs(diff) < BALANCE_EPSILON,
  };
}

/** Bilans jednej grupy (jednej „pozycji") — przekaż WYŁĄCZNIE wiersze tej grupy
 * (np. `groupDecreeLines(lines).find(...).lines`). */
export function computeGroupBalance(lines: readonly DecreeLineLike[]): DecreeBalance {
  return computeBalance(lines);
}

/** Bilans CAŁEGO dekretu — ΣWn vs ΣMa wszystkich wierszy (wszystkich grup razem). */
export function computeOverallBalance(lines: readonly DecreeLineLike[]): DecreeBalance {
  return computeBalance(lines);
}

/**
 * Auto-domykanie bilansu grupy (zachowanie z WF desktop) — dopisuje NOWY wiersz na
 * stronie, która ma NIŻSZĄ sumę, z kwotą równą różnicy. Konto NIE jest zgadywane
 * (żadne konto nie jest „domyślne" dla dowolnej pozycji) — nowy wiersz zostaje z
 * pustym kontem, user musi je wybrać przed zapisem/zatwierdzeniem dekretu. No-op
 * (zwraca `lines` bez zmian) gdy grupa jest już zbilansowana lub nie istnieje.
 */
export function autoBalanceGroup(
  lines: readonly DecreeLineDraft[],
  groupNo: number,
): DecreeLineDraft[] {
  const groupLines = lines.filter((line) => line.groupNo === groupNo);
  if (groupLines.length === 0) return [...lines];
  const balance = computeGroupBalance(groupLines);
  if (balance.balanced) return [...lines];
  // diff = debit - credit; dodatnie → Wn ma nadwyżkę → domykamy po stronie Ma, i odwrotnie.
  const deficientSide: DecreeLineSide = balance.diff > 0 ? 'credit' : 'debit';
  const closingLine: DecreeLineDraft = {
    ...emptyDecreeLine(groupNo, deficientSide),
    amount: Math.abs(balance.diff).toFixed(2),
  };
  return [...lines, closingLine];
}
